"use client";

import { useState } from 'react';
import { Facebook, Link as LinkIcon, Check, Share2 } from 'lucide-react';

interface SocialShareButtonsProps {
    theme?: 'light' | 'dark';
}

export function SocialShareButtons({ theme = 'light' }: SocialShareButtonsProps) {
    const [copied, setCopied] = useState(false);
    const isDark = theme === 'dark';
    const textBase = isDark ? 'text-slate-400' : 'text-slate-500';
    const textHover = isDark ? 'hover:text-white' : 'hover:text-slate-900';

    const url = "https://smokefree.aeaeng.com/"; // Fixed URL for sharing

    const handleCopy = () => {
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleFacebookShare = () => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    };

    const handleLineShare = () => {
        window.open(`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(url)}`, '_blank');
    };

    return (
        <div className="flex flex-col items-center gap-2 mt-4">
            <p className={`text-[10px] font-bold uppercase tracking-widest ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Share this App
            </p>
            <div className="flex items-center gap-3">
                {/* Line Share */}
                <button
                    onClick={handleLineShare}
                    className={`p-2 rounded-full transition-all ${isDark ? 'bg-slate-800 hover:bg-[#06C755]' : 'bg-slate-100 hover:bg-[#06C755]'} group`}
                    title="Share on Line"
                >
                    {/* Custom Line Icon SVG */}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-600'} group-hover:text-white transition-colors`}>
                        <path d="M20.3,10C20.3,16,14.9,20.8,8.8,20.8c-0.9,0-2.8,0.3-4.3,1.3c0,0-0.3,0.1-0.4,0c-0.1-0.1-0.2-0.3-0.1-0.4 c0.3-0.9,1-2.2,1.1-2.5c-2.8-1.7-4.2-4.2-4.2-6.9C0.9,6.4,6.2,1.6,12.3,1.6C18.4,1.6,20.3,6.4,20.3,10z M6.9,8.5 c-0.5,0-0.7,0.4-0.7,0.6v3.2c0,0.4,0.3,0.6,0.7,0.6s0.7-0.4,0.7-0.6v-1h1.3c0.4,0,0.6-0.3,0.6-0.7s-0.2-0.6-0.6-0.6H6.9z M19.4,11.7 c0-0.4-0.3-0.6-0.7-0.6h-2.5c-0.4,0-0.7,0.3-0.7,0.6v3.2c0,0.4,0.3,0.6,0.7,0.6s0.7-0.4,0.7-0.6v-1h1.8c0.4,0,0.6-0.3,0.6-0.7 s-0.2-0.6-0.6-0.6h-1.8V9.1h1.8c0.4,0,0.6-0.3,0.6-0.7c0-0.2-0.1-0.4-0.3-0.5C19.7,7.8,19.6,7.8,19.4,11.7z M12.8,12.9l0-4.3 c0-0.4-0.3-0.6-0.7-0.6c-0.4,0-0.7,0.3-0.7,0.6v5.6l0,0c0,0.1,0,0.1,0,0.2c0,0.1,0.1,0.2,0.1,0.3c0,0,0,0,0,0 c0.1,0.1,0.2,0.1,0.3,0.1h2.5c0.4,0,0.7-0.3,0.7-0.6s-0.3-0.6-0.7-0.6H12.8z M9,9.1v3.2c0,0.4,0.3,0.6,0.7,0.6s0.7-0.4,0.7-0.6V9.1 c0-0.4-0.3-0.6-0.7-0.6S9,8.7,9,9.1z" />
                    </svg>
                </button>

                {/* Facebook Share */}
                <button
                    onClick={handleFacebookShare}
                    className={`p-2 rounded-full transition-all ${isDark ? 'bg-slate-800 hover:bg-[#1877F2]' : 'bg-slate-100 hover:bg-[#1877F2]'} group`}
                    title="Share on Facebook"
                >
                    <Facebook size={16} className={`${isDark ? 'text-slate-400' : 'text-slate-600'} group-hover:text-white transition-colors`} />
                </button>

                {/* Copy Link */}
                <button
                    onClick={handleCopy}
                    className={`p-2 rounded-full transition-all ${isDark ? 'bg-slate-800 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200'} group relative`}
                    title="Copy Link"
                >
                    {copied ? (
                        <Check size={16} className="text-green-500" />
                    ) : (
                        <LinkIcon size={16} className={`${isDark ? 'text-slate-400' : 'text-slate-600'} group-hover:text-slate-900 transition-colors`} />
                    )}

                    {/* Tooltip */}
                    {copied && (
                        <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-[10px] bg-black text-white px-2 py-1 rounded shadow-lg whitespace-nowrap">
                            Copied!
                        </span>
                    )}
                </button>
            </div>
        </div>
    );
}
